// src/pages/AdminCommissionSettings.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaChartPie, FaFileCsv, FaPercentage, FaSave, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { fetchAdminCommissionDashboard, formatMoneyCents, updateCommissionSetting, type CommissionSetting } from '../lib/commissions';
import ForfeoPartnerPayoutReadiness from '../components/network/ForfeoPartnerPayoutReadiness';

const DEFAULT_LABELS: Record<string, string> = {
  combo: 'Combos Forfeo',
  forfait: 'Forfaits marchands',
  carte_universelle: 'Carte Accès Privilège Forfeo',
  last_call: 'Last Call',
  prevente: 'Prévente',
  boutique: 'Boutique Forfeo',
};

export default function AdminCommissionSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [days, setDays] = useState(90);
  const [dashboard, setDashboard] = useState<any>(null);
  const [settings, setSettings] = useState<CommissionSetting[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchAdminCommissionDashboard(days);
      setDashboard(data);
      setSettings(data?.settings || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [days]);

  const totals = dashboard?.totals || { gross_amount_cents: 0, commission_amount_cents: 0, net_amount_cents: 0, event_count: 0 };
  const byType = dashboard?.by_type || [];

  const settingsWithDefaults = useMemo(() => {
    const keys = ['combo', 'forfait', 'carte_universelle', 'last_call', 'prevente', 'boutique'];
    return keys.map((key) => settings.find((item) => item.product_type === key) || {
      id: key,
      product_type: key,
      label: DEFAULT_LABELS[key] || key,
      commission_rate: key === 'combo' ? 8 : 0,
      fixed_fee_cents: 0,
      is_active: true,
      legal_note: '',
    });
  }, [settings]);

  async function saveSetting(setting: CommissionSetting) {
    setSaving(setting.product_type);
    setMessage(null);
    try {
      await updateCommissionSetting(setting);
      setMessage(`Commission ${setting.label} sauvegardée.`);
      await load();
    } catch (err: any) {
      setMessage(err?.message || 'Erreur pendant la sauvegarde.');
    } finally {
      setSaving(null);
    }
  }

  function updateLocal(productType: string, patch: Partial<CommissionSetting>) {
    setSettings((current) => {
      const exists = current.some((item) => item.product_type === productType);
      if (!exists) return [...current, { id: productType, product_type: productType, label: DEFAULT_LABELS[productType] || productType, commission_rate: 0, fixed_fee_cents: 0, is_active: true, ...patch } as CommissionSetting];
      return current.map((item) => item.product_type === productType ? { ...item, ...patch } : item);
    });
  }

  function exportCsv() {
    const rows = [['type', 'gross', 'commission', 'net', 'count'], ...byType.map((item: any) => [item.source_type, item.gross_amount_cents / 100, item.commission_amount_cents / 100, item.net_amount_cents / 100, item.event_count])];
    const csv = rows.map((r) => r.map((c: any) => `"${String(c).replaceAll('"','""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'forfeo-commissions.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) {
    return <div className="rounded-3xl bg-white p-8 text-center text-slate-600"><FaSpinner className="mx-auto mb-3 animate-spin text-3xl text-blue-600" />Chargement des commissions...</div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in">
      <section className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 p-7 text-white shadow-2xl">
        <p className="text-xs font-black uppercase tracking-[0.35em] text-blue-200">Admin Forfeo</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-black md:text-4xl">Commissions & revenus</h2>
            <p className="mt-2 max-w-3xl text-sm text-blue-50/80">Configure les pourcentages prélevés par Forfeo et supervise les revenus générés par les Combos, cartes, forfaits, Last Call et autres produits.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[30, 90, 365].map((value) => <button key={value} onClick={() => setDays(value)} className={`rounded-full px-4 py-2 text-sm font-black ${days === value ? 'bg-white text-slate-950' : 'bg-white/10 hover:bg-white/20'}`}>{value} j</button>)}
            <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-4 py-2 text-sm font-black text-slate-950"><FaFileCsv /> CSV</button>
          </div>
        </div>
      </section>

      <ForfeoPartnerPayoutReadiness
        title="Forfeo encaisse, suit et prépare les règlements"
        subtitle="Ce panneau prépare la couche Forfeo Network : commissions par produit, montants nets partenaires, suivis PRP et paiements futurs sans modifier les flux réels actuels."
      />

      {message && <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm font-bold text-blue-900">{message}</div>}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaChartPie className="mb-3 text-2xl text-blue-600" /><p className="text-xs font-bold uppercase text-slate-400">Ventes brutes</p><p className="text-2xl font-black">{formatMoneyCents(totals.gross_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaPercentage className="mb-3 text-2xl text-indigo-600" /><p className="text-xs font-bold uppercase text-slate-400">Commissions Forfeo</p><p className="text-2xl font-black">{formatMoneyCents(totals.commission_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaShieldAlt className="mb-3 text-2xl text-emerald-600" /><p className="text-xs font-bold uppercase text-slate-400">Net marchands</p><p className="text-2xl font-black">{formatMoneyCents(totals.net_amount_cents)}</p></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"><FaChartPie className="mb-3 text-2xl text-amber-600" /><p className="text-xs font-bold uppercase text-slate-400">Événements</p><p className="text-2xl font-black">{totals.event_count || 0}</p></div>
      </div>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-black text-slate-950">Pourcentages configurables</h3>
        <p className="mt-1 text-sm text-slate-500">Par défaut, les Combos Forfeo sont à 8 %. Chaque acceptation de marchand conserve le taux applicable pour protéger Forfeo.</p>
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {settingsWithDefaults.map((setting) => (
            <div key={setting.product_type} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-start justify-between gap-3">
                <div><p className="text-xs font-black uppercase tracking-wide text-slate-400">{setting.product_type}</p><h4 className="text-lg font-black text-slate-950">{setting.label}</h4></div>
                <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={setting.is_active} onChange={(e) => updateLocal(setting.product_type, { is_active: e.target.checked })} /> Actif</label>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <label className="text-sm font-bold text-slate-700">Commission %<input type="number" min="0" max="50" step="0.1" value={setting.commission_rate} onChange={(e) => updateLocal(setting.product_type, { commission_rate: Number(e.target.value) })} className="mt-1 w-full rounded-2xl border border-slate-200 p-3" /></label>
                <label className="text-sm font-bold text-slate-700">Frais fixe ($)<input type="number" min="0" step="0.01" value={Number(setting.fixed_fee_cents || 0) / 100} onChange={(e) => updateLocal(setting.product_type, { fixed_fee_cents: Math.round(Number(e.target.value || 0) * 100) })} className="mt-1 w-full rounded-2xl border border-slate-200 p-3" /></label>
              </div>
              <label className="mt-3 block text-sm font-bold text-slate-700">Note légale / commerciale<textarea value={setting.legal_note || ''} onChange={(e) => updateLocal(setting.product_type, { legal_note: e.target.value })} className="mt-1 min-h-[78px] w-full rounded-2xl border border-slate-200 p-3" /></label>
              <button onClick={() => saveSetting(setting)} disabled={saving === setting.product_type} className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-black text-white disabled:opacity-50"><FaSave /> {saving === setting.product_type ? 'Sauvegarde...' : 'Sauvegarder'}</button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-black text-slate-950">Répartition par produit</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead><tr className="border-b text-xs uppercase text-slate-400"><th className="py-3">Produit</th><th>Ventes</th><th>Commission</th><th>Net</th><th>Nb</th></tr></thead>
            <tbody>
              {byType.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-500">Aucun événement pour la période.</td></tr>}
              {byType.map((item: any) => <tr key={item.source_type} className="border-b last:border-0"><td className="py-3 font-black">{DEFAULT_LABELS[item.source_type] || item.source_type}</td><td>{formatMoneyCents(item.gross_amount_cents)}</td><td>{formatMoneyCents(item.commission_amount_cents)}</td><td>{formatMoneyCents(item.net_amount_cents)}</td><td>{item.event_count}</td></tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
